import { useCallback, useEffect, useMemo, useState } from "react";

import { Message, MessageFolder } from "@asd/customer-portal-api";
import { formatDateTime, parseTimestamp, ReportKey } from "@asd/customer-portal-ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import {
  alpha,
  Avatar,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import { useProfileLink } from "~/hooks/useProfileLink";
import { useMessages } from "~/providers/MessagesProvider";
import { COLUMNS, FOLDER_ICONS, FOLDERS, fromGridModel, renderMessageBody, toGridModel } from "./helpers";
import MoveToMenu from "./MoveToMenu";
import ToggleReadButton from "./ToggleReadButton";

const MessageCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeFolder = (location.state?.folder as MessageFolder) ?? FOLDERS[0];
  const openMessageId = (location.state?.messageId as string | null) ?? null;

  const { onProfileNavigate } = useProfileLink();
  const { messages, isLoading, hasError, moveMessages, updateMessagesReadStatus } = useMessages();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const folderMessages = useMemo(() => messages[activeFolder] ?? [], [messages, activeFolder]);

  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return folderMessages;
    const q = searchQuery.toLowerCase();
    return folderMessages.filter((m) => m.subject.toLowerCase().includes(q) || m.body.toLowerCase().includes(q));
  }, [folderMessages, searchQuery]);

  const openMessage = useMemo(() => folderMessages.find((m) => m.id === openMessageId) ?? null, [folderMessages, openMessageId]);
  const openMessageIndex = useMemo(() => folderMessages.findIndex((m) => m.id === openMessageId), [folderMessages, openMessageId]);
  const selectedIdArray = useMemo<string[]>(() => Array.from(selectedIds), [selectedIds]);
  const selectedMessages = useMemo(() => folderMessages.filter((m) => selectedIds.has(m.id)), [folderMessages, selectedIds]);

  const anySelectedRead = selectedMessages.some((m) => m.isRead);
  const hasSelection = selectedIdArray.length > 0;

  const toolbarMode: "default" | "selection" | "detail" = openMessage ? "detail" : hasSelection ? "selection" : "default";

  const updateRouteState = useCallback(
    (folder: MessageFolder, messageId: string | null = null) => {
      navigate(".", { state: { folder, messageId } });
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (id: string) => {
      const msg = folderMessages.find((m) => m.id === id);
      if (msg && !msg.isRead) {
        updateMessagesReadStatus([id], true);
      }
      updateRouteState(activeFolder, id);
    },
    [folderMessages, updateRouteState, activeFolder, updateMessagesReadStatus],
  );

  const handleBackToFolder = useCallback(() => {
    updateRouteState(activeFolder, null);
  }, [activeFolder, updateRouteState]);

  const handleNavigate = useCallback(
    (dir: -1 | 1) => {
      const next = folderMessages[openMessageIndex + dir];
      if (next) {
        handleRowClick(next.id);
      }
    },
    [folderMessages, openMessageIndex, handleRowClick],
  );

  const handleToggleRead = useCallback(
    (ids: string[], markAsRead: boolean) => {
      updateMessagesReadStatus(ids, markAsRead);
    },
    [updateMessagesReadStatus],
  );

  const handleMove = useCallback(
    (ids: string[], target: MessageFolder) => {
      moveMessages(ids, target);
      setSelectedIds(new Set());
      if (openMessageId && ids.includes(openMessageId)) {
        updateRouteState(activeFolder, null);
      }
    },
    [moveMessages, openMessageId, updateRouteState, activeFolder],
  );

  const handleNavigateToReport = useCallback(
    (reportKey: ReportKey, monthValue: string) => {
      navigate("/reports", { state: { reportKey, monthValue } });
    },
    [navigate],
  );

  useEffect(() => {
    setSelectedIds(new Set());
    setSearchQuery("");
  }, [activeFolder, openMessageId]);

  return (
    <Box sx={{ height: "100%", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
      <Stack direction="row" gap={1} p={1} sx={{ height: "100%", overflow: "hidden" }}>
        <Paper
          elevation={6}
          sx={{
            width: 170,
            flexShrink: 0,
            zIndex: 1,
            bgcolor: "action.hover",
          }}
        >
          <Stack
            direction="column"
            sx={{
              width: 170,
              flexShrink: 0,
              bgcolor: "action.hover",
            }}
          >
            {FOLDERS.map((folder) => {
              const unreadCount = messages[folder]?.filter((m) => m.folder === folder && !m.isRead).length || 0;
              const isActive = folder === activeFolder;

              return (
                <ListItem key={folder} sx={{ display: "block" }} disablePadding>
                  <ListItemButton onClick={() => updateRouteState(folder, null)} selected={isActive}>
                    <ListItemIcon sx={{ minWidth: 0, mr: 1, justifyContent: "center" }}>{FOLDER_ICONS[folder]}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack alignItems="center" direction="row" gap={1}>
                          {folder}
                          {unreadCount > 0 && (
                            <>
                              <Box sx={{ flexGrow: 1 }} />
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: "1rem",
                                  bgcolor: "action.selected",
                                  color: "text.primary",
                                }}
                              >
                                {unreadCount}
                              </Avatar>
                            </>
                          )}
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Stack>
        </Paper>
        {hasError && (
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography color="error">An error occurred while fetching your messages. Please try again later.</Typography>
          </Paper>
        )}
        {!hasError && (
          <Paper elevation={1} sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "auto" }}>
            <Toolbar
              sx={{
                gap: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
                pl: 2,
                pr: 1,
              }}
              variant="dense"
              disableGutters
            >
              {toolbarMode === "default" && (
                <>
                  <Typography fontWeight={600} variant="subtitle1">
                    {activeFolder}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <TextField
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search"
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Clear Search">
                              <IconButton
                                onClick={() => setSearchQuery("")}
                                size="small"
                                sx={{ visibility: searchQuery ? "visible" : "hidden" }}
                              >
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ width: 400, mr: -0.5 }}
                    value={searchQuery}
                  />
                </>
              )}
              {toolbarMode === "selection" && (
                <>
                  <Typography color="text.secondary" sx={{ mr: 2 }} variant="body2">
                    {selectedIdArray.length} selected
                  </Typography>
                  <ToggleReadButton isRead={anySelectedRead} onClick={() => handleToggleRead(selectedIdArray, !anySelectedRead)} />
                  <MoveToMenu currentFolder={activeFolder} onMove={(target) => handleMove(selectedIdArray, target)} />
                  <Box sx={{ flex: 1 }} />
                  <TextField
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search"
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Clear Search">
                              <IconButton
                                onClick={() => setSearchQuery("")}
                                size="small"
                                sx={{ visibility: searchQuery ? "visible" : "hidden" }}
                              >
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ width: 400, mr: -0.5 }}
                    value={searchQuery}
                  />
                </>
              )}
              {toolbarMode === "detail" && openMessage && (
                <>
                  <Tooltip sx={{ mr: 2 }} title={`Back to ${activeFolder}`}>
                    <IconButton onClick={handleBackToFolder} size="small">
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>
                  <ToggleReadButton
                    onClick={() => {
                      handleToggleRead([openMessage.id], false);
                      handleBackToFolder();
                    }}
                    isRead
                  />
                  <MoveToMenu currentFolder={activeFolder} onMove={(target) => handleMove([openMessage.id], target)} />
                  <Box sx={{ flex: 1 }} />
                  <Typography color="text.secondary" sx={{ whiteSpace: "nowrap" }} variant="body2">
                    {openMessageIndex + 1} of {folderMessages.length}
                  </Typography>
                  <Tooltip title={openMessageIndex <= 0 ? "" : "Previous message"}>
                    <IconButton disabled={openMessageIndex <= 0} onClick={() => handleNavigate(-1)} size="small">
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={openMessageIndex >= folderMessages.length - 1 ? "" : "Next message"}>
                    <IconButton disabled={openMessageIndex >= folderMessages.length - 1} onClick={() => handleNavigate(1)} size="small">
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Toolbar>
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {openMessage ? (
                <Box sx={{ display: "grid", gridTemplateRows: "auto auto auto 1fr", flex: 1, p: 3, height: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    {openMessage.subject}
                  </Typography>
                  <Typography color="text.secondary" display="block" variant="caption" gutterBottom>
                    {openMessage.date ? formatDateTime(parseTimestamp(openMessage.date)) : ""}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography component="div" sx={{ whiteSpace: "pre-wrap", overflow: "auto" }} variant="body1">
                    {renderMessageBody(openMessage.body, handleNavigateToReport, onProfileNavigate)}
                  </Typography>
                </Box>
              ) : (
                <DataGrid<Message>
                  columns={COLUMNS}
                  getRowClassName={(params) => (params.row.isRead ? "msg-read" : "msg-unread")}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 },
                  }}
                  loading={isLoading}
                  onRowClick={(params) => {
                    handleRowClick(params.row.id);
                  }}
                  onRowSelectionModelChange={(model) => {
                    const m = model as { type: string; ids: Set<unknown> };
                    setSelectedIds(
                      fromGridModel(
                        m,
                        filteredMessages.map((msg) => msg.id),
                      ),
                    );
                  }}
                  pageSizeOptions={[5, 10, 25]}
                  rows={filteredMessages}
                  rowSelectionModel={toGridModel(selectedIds)}
                  showToolbar={false}
                  slotProps={{
                    loadingOverlay: {
                      variant: "circular-progress",
                      noRowsVariant: "circular-progress",
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Typography
                        sx={{
                          justifySelf: "center",
                          height: "100%",
                          alignContent: "center",
                        }}
                      >
                        You have no messages in your {activeFolder}
                      </Typography>
                    ),
                  }}
                  sx={{
                    "& .MuiDataGrid-row:hover": { cursor: "pointer" },
                    "& .msg-unread": {
                      fontWeight: "bold",
                      bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
                    },
                  }}
                  checkboxSelection
                  disableColumnFilter
                  disableColumnMenu
                  disableColumnSorting
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                />
              )}
            </Box>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default MessageCenter;
